import { find, get, isEqual, isNil, keys, remove, set } from 'lodash';
import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { KeyValue } from 'sr-types/lib/search/v1/graphql';
import { I18nContext, I18nContextType } from '../i18n/I18n';
import Validator, { ValidationState } from './Validator';
import { useRegisterCreateDefault } from './useRegisterCreateDefault';

export type FormFieldPath<S extends Record<string, unknown> = any> = keyof S | string;

export type HandleChangeType<S extends Record<string, unknown> = any> = (
    prop: FormFieldPath<S>,
    value: any,
    coerceChangeToArray?: boolean,
    mapTo?: string
) => Promise<S | undefined>;

export type FormFieldChange<S extends Record<string, unknown> = any> = {
    prop: FormFieldPath<S>;
    value: any;
    coerceChangeToArray?: boolean;
    mapTo?: string;
};

export type HandleChangesType<S extends Record<string, unknown> = any> = (
    changes: FormFieldChange<S>[]
) => Promise<S | undefined>;

export type FormContextType<S extends Record<string, unknown> = any> = {
    loading: boolean;
    state: S;
    setState: Dispatch<SetStateAction<S>>;
    validation: ValidationState;
    handleChange: HandleChangeType<S>;
    handleChanges: HandleChangesType<S>;
    expandedSections: string[];
    setExpandedSections: (sectionNames: string[]) => void;
    allowMultipleExpansion?: boolean;
    registerCreateDefault?: (prop: string, value: unknown) => void;
};

const noopHandleChange: HandleChangeType = async () => undefined;
const noopHandleChanges: HandleChangesType = async () => undefined;
const noopSetState: Dispatch<SetStateAction<any>> = () => {};
const noopSetExpandedSections = () => {};

export const FormContext = createContext<FormContextType>({
    validation: undefined,
    handleChange: noopHandleChange,
    handleChanges: noopHandleChanges,
    state: undefined,
    setState: noopSetState,
    expandedSections: [],
    setExpandedSections: noopSetExpandedSections,
    loading: false,
    allowMultipleExpansion: false,
    registerCreateDefault: undefined
});

export type Listener = {
    prop: string | string[];
    func: (name, newValue, newState) => void | Promise<any>;
};

type FormProviderProps = {
    /** True if a loading operation is currently exectuting */
    loading?: boolean;
    /** Full state of this context */
    state: any;
    /** State setter */
    setState?: any;
    /** Validation rules for this state */
    validationRules?: any;
    /** State change listeners. Exectued after each field change but before React's render */
    listeners?: Listener[];
    /** Sections (ids) that will be initially expanded */
    expandedSections?: string[];
    /** Should multiple sections be allowed to expand? */
    allowMultipleExpansion?: boolean;
    /**
     * Opt out of functional state updates for `handleChange` and create-default registration.
     * Defaults to false (functional updates on). Set when the setter only accepts object values,
     * or the call site is known to break with functional updaters.
     */
    disableFunctionalStateUpdate?: boolean;
    children: any;
};

export const resolve = (state, prop, mapTo) => {
    const val = get(state, prop);
    if (mapTo) {
        const kv: KeyValue = find(val, (e) => e.key === mapTo);
        return kv ? kv.value : undefined;
    } else {
        return val;
    }
};

async function runListeners(newState, prop, value, listeners: Listener[]) {
    if (listeners && listeners.length) {
        let syncListeners = 0;
        let asyncListeners = 0;
        const promises = [];
        listeners.forEach((listener) => {
            const arr = Array.isArray(listener.prop) ? listener.prop : [listener.prop];
            arr.forEach((l) => {
                if (l === prop) {
                    const ret = listener.func(prop, value, newState);
                    // Is this function a promise.
                    if (ret && ret.then && typeof ret.then === 'function') {
                        promises.push(ret);
                        asyncListeners++;
                    } else {
                        syncListeners++;
                    }
                }
            });
        });
        if (promises.length) {
            await Promise.all(promises);
        }
        // log('Executed', syncListeners, 'sync listeners and', asyncListeners, 'async listeners');
    }
}

function setValue(state, prop, value, mapTo, coerceChangeToArray) {
    if (mapTo) {
        const map: KeyValue[] = get(state, prop) || [];
        remove(map, (e) => e.key === mapTo);
        map.push({ key: mapTo, value: value });
        set(state, prop, map);
    } else if (coerceChangeToArray && !Array.isArray(value)) {
        set(state, prop, isNil(value) ? [] : [value]);
    } else {
        set(state, prop, value);
    }
}

export const FormProvider = (props: FormProviderProps) => {
    const {
        loading,
        validationRules,
        state,
        setState,
        listeners,
        children,
        allowMultipleExpansion = false,
        disableFunctionalStateUpdate = false,
        expandedSections: defaultExpandedSections
    } = props;
    const [validator, setValidator] = useState<Validator>();
    const [validation, setValidation] = useState<ValidationState>();
    const [expandedSections, setExpandedSections] = useState<string[]>(defaultExpandedSections);
    const i18nContext: I18nContextType = useContext(I18nContext);
    const registerCreateDefault = useRegisterCreateDefault(disableFunctionalStateUpdate ? undefined : setState);

    useEffect(() => {
        setValidator(
            new Validator(validationRules, {
                // If true validator will keep going even after first field fails validation.
                runAll: false,
                i18nContext: i18nContext
            })
        );
    }, [i18nContext, validationRules]);

    useEffect(() => {
        if (validator) {
            const validation = validator.validate(state);
            // console.log('Initial validation:', validation, 'state:', state);
            setValidation(validation);
        }
    }, [state, validator]);

    const handleChange = async (prop, value, coerceChangeToArray, mapTo = undefined) => {
        if (typeof setState !== 'function') {
            return undefined;
        }

        if (!disableFunctionalStateUpdate) {
            setState((current) => {
                const currentValue = resolve(current, prop, mapTo);
                if (isEqual(currentValue, value)) {
                    return current;
                }

                const newState = { ...current };
                setValue(newState, prop, value, mapTo, coerceChangeToArray);
                runListeners(newState, prop, value, listeners);
                if (validator) {
                    const validation = validator.validate(newState, prop);
                    setValidation(validation);
                }
                return newState;
            });
            return;
        }

        const newState = { ...state };
        const currentValue = resolve(newState, prop, mapTo);
        if (!isEqual(currentValue, value)) {
            setValue(newState, prop, value, mapTo, coerceChangeToArray);
            runListeners(newState, prop, value, listeners);
            if (validator) {
                const validation = validator.validate(newState, prop);
                setValidation(validation);
            }
            setState(newState);
            return newState;
        }
    };

    const handleChanges = async (changes) => {
        const newState = { ...state };
        const appliedChanges = [];

        changes.forEach(({ prop, value, coerceChangeToArray, mapTo }) => {
            const currentValue = resolve(newState, prop, mapTo);
            if (isEqual(currentValue, value)) {
                return;
            }

            setValue(newState, prop, value, mapTo, coerceChangeToArray);
            appliedChanges.push({ prop, value });
        });

        if (!appliedChanges.length) {
            return undefined;
        }

        appliedChanges.forEach(({ prop, value }) => {
            runListeners(newState, prop, value, listeners);
        });

        if (validator) {
            const validation = validator.validate(newState);
            setValidation(validation);
        }

        setState(newState);
        return newState;
    };

    return (
        <FormContext.Provider
            value={{
                loading,
                validation,
                handleChange,
                handleChanges,
                state,
                setState,
                expandedSections,
                setExpandedSections,
                allowMultipleExpansion,
                registerCreateDefault
            }}
        >
            {children}
        </FormContext.Provider>
    );
};

const flattenObj = (ob) => {
    let result = {};
    for (const i in ob) {
        if (typeof ob[i] === 'object' && !Array.isArray(ob[i])) {
            const temp = flattenObj(ob[i]);
            for (const j in temp) {
                result[i + '.' + j] = temp[j];
            }
        } else {
            result[i] = ob[i];
        }
    }
    return result;
};

export const getKeys = (state) => {
    return keys(flattenObj(state));
};
